import { useState, useCallback } from 'react';
import {
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { PlanElement } from '@/services/api';
import { isPlanRadSortable } from '@/lib/planDndUtils';

/**
 * Custom hook for managing drag-and-drop logic in PlanEditor
 * 
 * Extracted from PlanEditor.tsx to separate drag-and-drop concerns.
 * Handles drag start, drag over, drag end, and drag cancel events.
 * Provides sensors configuration and active element state.
 */

interface UsePlanDragDropProps {
  elements: PlanElement[];
  setElementsOptimistic: (updater: (prev: PlanElement[]) => PlanElement[]) => void;
  updateMultipleRekkefølge: (updates: Array<{ id: number; rekkefølge: number; parentId?: number }>) => Promise<boolean>;
  updateElement: (
    elementId: number,
    type: 'overskrift' | 'hendelse' | 'sang' | 'musikk_placeholder',
    data: Record<string, unknown>
  ) => Promise<boolean>;
  onSuccess?: () => void;
  setSuccess?: (msg: string | null) => void;
  setError?: (msg: string | null) => void;
  expandHeader?: (headerId: number) => void;
  readOnly?: boolean;
}

export const usePlanDragDrop = ({
  elements,
  setElementsOptimistic,
  updateMultipleRekkefølge,
  updateElement,
  onSuccess,
  setSuccess,
  setError,
  expandHeader,
  readOnly = false,
}: UsePlanDragDropProps) => {
  const [activeElement, setActiveElement] = useState<PlanElement | null>(null);

  // Configure drag sensors - deaktiver når readOnly er true ved å sette stor avstand
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: readOnly ? 9999 : 8, // Deaktiver ved å sette stor avstand når readOnly
    },
  });
  
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  
  // Bruk kun pointer sensor når readOnly er true (keyboard sensor vil fortsatt fungere, men pointer er hovedmetoden)
  const sensors = useSensors(
    pointerSensor,
    keyboardSensor
  );

  /**
   * Handle drag start - set active element and auto-expand parent if needed
   */
  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (readOnly) return; // Ignorer drag når readOnly er true
    
    const { active } = event;
    const element = elements.find(el => el.id === active.id);
    setActiveElement(element || null);
    
    // Auto-expand overskrifter for bedre UX
    if (
      element &&
      isPlanRadSortable(element) &&
      element.parent_id != null &&
      expandHeader
    ) {
      expandHeader(element.parent_id);
    }
  }, [elements, expandHeader, readOnly]);

  /**
   * Handle drag over - auto-expand overskrift when hovering
   */
  const handleDragOver = useCallback((event: any) => {
    if (readOnly) return; // Ignorer drag når readOnly er true
    
    const { over } = event;
    
    if (over?.id && expandHeader) {
      const overElement = elements.find(el => el.id === over.id);
      if (overElement?.type === 'overskrift') {
        expandHeader(overElement.id);
      }
    }
  }, [elements, expandHeader, readOnly]);

  /**
   * Handle drag cancel - clear active element
   */
  const handleDragCancel = useCallback(() => {
    setActiveElement(null);
  }, []);

  /**
   * Handle overskrift drag-and-drop (reorder overskrifter)
   */
  const handleOverskriftDrag = useCallback(async (
    active: any,
    over: any,
    overskriftList: PlanElement[]
  ) => {
    const oldIndex = overskriftList.findIndex(h => h.id === active.id);
    const newIndex = overskriftList.findIndex(h => h.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(overskriftList, oldIndex, newIndex);
    
    // Optimistic update
    setElementsOptimistic(prevElements => 
      prevElements.map(el => {
        if (el.type === 'overskrift') {
          const newPos = newOrder.findIndex(h => h.id === el.id);
          return { ...el, rekkefølge: newPos };
        }
        return el;
      })
    );

    // Backend update
    const updates = newOrder.map((overskrift, index) => ({
      id: overskrift.id,
      rekkefølge: index,
    }));

    const success = await updateMultipleRekkefølge(updates);
    
    if (!success && setError) {
      setError('Kunne ikke oppdatere rekkefølge');
    }
  }, [setElementsOptimistic, updateMultipleRekkefølge, setError]);

  /**
   * Handle hendelse reorder within same parent
   */
  const handleHendelseReorder = useCallback(async (
    activeElement: PlanElement,
    targetParentId: number,
    targetIndex: number,
    activeId: any
  ) => {
    const siblings = elements
      .filter(el => el.parent_id === targetParentId)
      .sort((a, b) => a.rekkefølge - b.rekkefølge);
    
    const oldIndex = siblings.findIndex(h => h.id === activeId);
    
    if (oldIndex === -1) return;
    
    const newOrder = arrayMove(siblings, oldIndex, targetIndex);
    
    // Optimistic update
    setElementsOptimistic(prevElements =>
      prevElements.map(el => {
        if (el.parent_id === targetParentId) {
          const newPos = newOrder.findIndex(h => h.id === el.id);
          return { ...el, rekkefølge: newPos };
        }
        return el;
      })
    );

    // Backend update
    const updates = newOrder.map((hendelse, index) => ({
      id: hendelse.id,
      rekkefølge: index,
    }));

    const success = await updateMultipleRekkefølge(updates);
    
    if (!success && setError) {
      setError('Kunne ikke oppdatere rekkefølge');
    }
  }, [elements, setElementsOptimistic, updateMultipleRekkefølge, setError]);

  /**
   * Handle hendelse move to new parent
   */
  const handleHendelseMove = useCallback(async (
    activeElement: PlanElement,
    targetParentId: number,
    targetIndex: number
  ) => {
    // First update the moved element's parent and rekkefølge
    let elementType: 'hendelse' | 'sang' | 'musikk_placeholder' = 'hendelse';
    if (activeElement.type === 'sang') {
      elementType = 'sang';
    } else if (activeElement.type === 'musikk_placeholder') {
      elementType = 'musikk_placeholder';
    }

    const success = await updateElement(activeElement.id, elementType, {
      parentId: targetParentId,
      rekkefølge: targetIndex,
    });

    if (!success) {
      if (setError) setError('Kunne ikke flytte innslag');
      return;
    }

    // Sorter søsken som i UI; targetIndex er posisjon i denne listen
    const newSiblings = elements
      .filter(el => el.parent_id === targetParentId && el.id !== activeElement.id)
      .sort((a, b) => a.rekkefølge - b.rekkefølge);
    newSiblings.splice(targetIndex, 0, { ...activeElement, parent_id: targetParentId });
    
    const updates = newSiblings.map((hendelse, index) => ({
      id: hendelse.id,
      rekkefølge: index,
    }));

    await updateMultipleRekkefølge(updates);

    if (setSuccess) {
      setSuccess('Innslag flyttet');
      setTimeout(() => setSuccess(null), 3000);
    }

    if (onSuccess) onSuccess();
  }, [elements, updateElement, updateMultipleRekkefølge, setSuccess, setError, onSuccess]);

  /**
   * Main drag end handler - routes to appropriate handler based on element types
   */
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    if (readOnly) {
      setActiveElement(null);
      return; // Ignorer drag når readOnly er true
    }
    
    const { active, over } = event;
    setActiveElement(null);

    if (!over || active.id === over.id) return;

    const activeElement = elements.find(el => el.id === active.id);
    const overElement = elements.find(el => el.id === over.id);

    if (!activeElement) return;

    // Håndter overskrift drag
    if (activeElement.type === 'overskrift' && overElement?.type === 'overskrift') {
      const overskrifter = elements
        .filter(el => el.type === 'overskrift')
        .sort((a, b) => a.rekkefølge - b.rekkefølge);
      
      await handleOverskriftDrag(active, over, overskrifter);
      return;
    }

    // Håndter hendelse drag
    if (isPlanRadSortable(activeElement)) {
      let targetParentId: number | null = null;
      let targetIndex: number;

      if (overElement && isPlanRadSortable(overElement)) {
        targetParentId = overElement.parent_id ?? null;
        // Må sortere som i handleHendelseReorder — ellers matcher ikke indeks visuell liste
        const siblings = elements
          .filter(el => el.parent_id === targetParentId)
          .sort((a, b) => a.rekkefølge - b.rekkefølge);
        targetIndex = siblings.findIndex(h => h.id === over.id);
      } else if (overElement?.type === 'overskrift') {
        targetParentId = overElement.id;
        targetIndex = 0;
      } else {
        return;
      }

      if (targetParentId === null) return;

      const oldParentId = activeElement.parent_id ?? null;

      if (oldParentId === targetParentId) {
        await handleHendelseReorder(activeElement, targetParentId, targetIndex, active.id);
      } else {
        await handleHendelseMove(activeElement, targetParentId, targetIndex);
      }
    }
  }, [elements, handleOverskriftDrag, handleHendelseReorder, handleHendelseMove, readOnly]);

  return {
    sensors,
    activeElement,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
};

